import { useRef, useMemo } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { UseVirtualTableOptions, UseVirtualTableReturn } from './types'

/**
 * Hook that seamlessly integrates TanStack Table with TanStack Virtual for efficient row virtualization.
 *
 * @example
 * ```tsx
 * const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() })
 * const { rows, virtualizer, containerRef } = useVirtualTable({
 *   table,
 *   estimateSize: 35,
 *   overscan: 5,
 * })
 * ```
 */
export function useVirtualTable<TData>({
  table,
  estimateSize,
  overscan = 5,
  getScrollElement,
  measureElement,
  rangeExtractor,
}: UseVirtualTableOptions<TData>): UseVirtualTableReturn {
  const containerRef = useRef<HTMLDivElement>(null)

  // Get the row count from the table
  const rowCount = table.getRowModel().rows.length

  // Normalize estimateSize to a function
  const estimateSizeFn = useMemo(() => {
    return typeof estimateSize === 'number'
      ? () => estimateSize
      : estimateSize
  }, [estimateSize])

  // Set up the virtualizer
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: getScrollElement || (() => containerRef.current),
    estimateSize: estimateSizeFn,
    overscan,
    measureElement,
    rangeExtractor,
  })

  return {
    rows: virtualizer.getVirtualItems(),
    virtualizer,
    containerRef,
  }
}
